import React, { useState, useRef, useEffect } from 'react';
import { Ticket, User } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { isTicketClosed } from '../../utils/statusUtils';

interface TicketCardProps {
  ticket: Ticket;
  columnIndex?: number;
  totalColumns?: number;
  onClick: () => void;
  onRefresh: () => void;
  dragListeners?: any;
  isDragging?: boolean;
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket, columnIndex = 0, totalColumns = 6, onClick, onRefresh, dragListeners, isDragging }) => {
  const { user } = useAuth();
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [agents, setAgents] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Load agents when dropdown is opened
  useEffect(() => {
    if (showAssignDropdown && agents.length === 0) {
      loadAgents();
    }
  }, [showAssignDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAssignDropdown(false);
      }
    };

    if (showAssignDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAssignDropdown]);

  const loadAgents = async () => {
    try {
      const fetchedAgents = await apiService.getAgents();
      setAgents(fetchedAgents);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const handleAssign = async (agentId: string | null) => {
    setLoading(true);
    try {
      await apiService.reassignTicket(ticket.id, agentId || '');
      setShowAssignDropdown(false);
      
      // Emit custom event for immediate tab bar refresh
      if (agentId === user?.id) {
        // User assigned ticket to themselves
        console.log('🚀 TicketCard: Emitting ticket-claimed event for self-assignment');
        window.dispatchEvent(new CustomEvent('ticket-claimed', {
          detail: { ticketId: ticket.id, agentId: user?.id }
        }));
      } else if (ticket.agentId === user?.id && agentId !== user?.id) {
        // User unassigned ticket from themselves
        console.log('🚀 TicketCard: Emitting ticket-unclaimed event for self-unassignment');
        window.dispatchEvent(new CustomEvent('ticket-unclaimed', {
          detail: { ticketId: ticket.id, agentId: user?.id }
        }));
      }
      
      onRefresh();
    } catch (error) {
      console.error('Error reassigning ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAssignDropdown(!showAssignDropdown);
  };

  const handleCardDoubleClick = (e: React.MouseEvent) => {
    console.log('🎯 Card double-clicked!', { ticketId: ticket.id, target: e.target });
    onClick();
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const formatCreatedDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplayName = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'new': 'New',
      'in_progress': 'In Progress',
      'in_progress_ai_agent': 'In Progress - AI Agent',
      'in_progress_tier1': 'In Progress - Tier 1',
      'in_progress_tier2': 'In Progress - Tier 2',
      'in_progress_tier3': 'In Progress - Tier 3',
      'escalated-awaiting-agent': 'Escalated - Awaiting Agent',
      'service': 'Service',
      'closed-resolved': 'Closed - Resolved',
      'closed-not-resolved': 'Closed - Not Resolved',
      'closed-partially-resolved': 'Closed - Partially Resolved',
      'closed-no-feedback': 'Closed - No Feedback',
      'closed-by-agent-resolved': 'Closed - Agent Resolved',
      'abandoned': 'Abandoned'
    };
    return statusMap[status] || status;
  };

  const formatTimeAgo = (dateString: string) => {
    const now = Date.now();
    const createdTime = new Date(dateString).getTime();
    const diffMs = now - createdTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'now';
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return `${Math.floor(diffDays / 7)}w`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getTierBadge = () => {
    const tierStatuses = ['in_progress_tier1', 'in_progress_tier2', 'in_progress_tier3'];
    if (!tierStatuses.includes(ticket.status as any)) {
      return null;
    }

    const tierMapping = {
      'in_progress_tier1': { label: 'Tier 1', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
      'in_progress_tier2': { label: 'Tier 2', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
      'in_progress_tier3': { label: 'Tier 3', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' }
    };

    const tier = tierMapping[ticket.status as keyof typeof tierMapping];
    if (!tier) return null;

    return (
      <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${tier.color}`}>
        {tier.label}
      </span>
    );
  };

  const getClosureStatusBadge = () => {
    const closedStatuses = ['closed-resolved', 'closed-not-resolved', 'closed-partially-resolved', 'closed-no-feedback', 'closed-by-agent-resolved', 'abandoned'];
    if (!closedStatuses.includes(ticket.status as any)) {
      return null;
    }

    const statusMapping = {
      'closed-resolved': { label: 'Resolved', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
      'closed-not-resolved': { label: 'Not Resolved', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
      'closed-partially-resolved': { label: 'Partial', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
      'closed-no-feedback': { label: 'No Feedback', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
      'closed-by-agent-resolved': { label: 'Agent Resolved', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
      'abandoned': { label: 'Abandoned', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' }
    };

    const status = statusMapping[ticket.status as keyof typeof statusMapping];
    if (!status) return null;

    return (
      <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
        {status.label}
      </span>
    );
  };

  const getUrgencyBadge = () => {
    // Don't show urgency badges for closed tickets
    const closedStatuses = ['closed-resolved', 'closed-not-resolved', 'closed-partially-resolved', 'closed-no-feedback', 'closed-by-agent-resolved', 'abandoned'];
    if (closedStatuses.includes(ticket.status as any)) {
      return null;
    }

    const now = Date.now();
    const createdTime = new Date(ticket.createdAt).getTime();
    const elapsedMinutes = Math.floor((now - createdTime) / (1000 * 60));
    const isAssigned = !!ticket.agentId;

    // AI handling check
    const isAIHandling = ticket.agentId === 'neuro-ai-agent' || ticket.status === 'in_progress_ai_agent';
    
    // Check if customer requested human
    const customerRequestedHuman = ticket.messages?.some(msg => {
      const content = msg.content?.toLowerCase() || '';
      return content.includes('human') || content.includes('person') || 
             content.includes('agent') || content.includes('representative');
    });

    if (isAIHandling && customerRequestedHuman && elapsedMinutes > 3) {
      return { label: 'AI', color: 'bg-red-500 text-white', urgent: true };
    }

    if (!isAssigned && elapsedMinutes > 5) {
      return { label: 'Overdue', color: 'bg-red-500 text-white', urgent: true };
    }

    if (isAIHandling) {
      return { label: 'AI', color: 'bg-blue-500 text-white', urgent: false };
    }

    if (ticket.status === 'escalated-awaiting-agent') {
      return { label: 'Escalated', color: 'bg-orange-500 text-white', urgent: true };
    }

    return null;
  };

const renderAgentAvatar = () => {
    // Don't show agent avatar for closed tickets since they can't be reassigned
    const closedStatuses = ['closed-resolved', 'closed-not-resolved', 'closed-partially-resolved', 'closed-no-feedback', 'closed-by-agent-resolved', 'abandoned'];
    if (closedStatuses.includes(ticket.status as any)) {
      return null;
    }
    
    // Don't show clickable avatar if user doesn't have edit permissions
    if (!user?.permissions?.includes('tickets.edit')) {
      // Show read-only avatar for assigned tickets
      if (ticket.agentId && ticket.agent) {
        const agent = ticket.agent;
        
        let avatarUrl = agent?.avatar || agent?.avatarUrl;
        // Convert relative avatar URL to full URL if needed
        if (avatarUrl && avatarUrl.startsWith('/uploads/avatars/')) {
          avatarUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${avatarUrl}`;
        }
        const initials = agent?.name 
          ? agent.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
          : 'AG';

        return (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-base font-medium overflow-hidden"
            title={`Assigned to ${agent?.name || 'Unknown Agent'}`}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={agent?.name || 'Agent'}
                className="w-full h-full object-cover rounded-full"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`w-full h-full bg-primary-500 rounded-full flex items-center justify-center text-white ${avatarUrl ? 'hidden' : ''}`}>
              {initials}
            </div>
          </div>
        );
      }
      return null; // Don't show anything for unassigned tickets if no edit permission
    }
    if (!ticket.agentId) {
      return (
        <button
          onClick={handleAvatarClick}
          className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-base font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
          title="Assign agent"
        >
          +
        </button>
      );
    }

    const agent = ticket.agent;
    
    let avatarUrl = agent?.avatar || agent?.avatarUrl;
    // Convert relative avatar URL to full URL if needed
    if (avatarUrl && avatarUrl.startsWith('/uploads/avatars/')) {
      avatarUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${avatarUrl}`;
    }
    const initials = agent?.name 
      ? agent.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
      : 'AG';

    return (
      <button
        onClick={handleAvatarClick}
        className="w-10 h-10 rounded-full flex items-center justify-center text-base font-medium hover:opacity-80 transition-all overflow-hidden"
        title={`Assigned to ${agent?.name || 'Unknown Agent'} - Click to reassign`}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={agent?.name || 'Agent'}
            className="w-full h-full object-cover rounded-full"
            onError={(e) => {
              // Fallback to initials if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`w-full h-full bg-primary-500 rounded-full flex items-center justify-center text-white ${avatarUrl ? 'hidden' : ''}`}>
          {initials}
        </div>
      </button>
    );
  };

  const urgencyBadge = getUrgencyBadge();
  const tierBadge = getTierBadge();
  const closureStatusBadge = getClosureStatusBadge();

  return (
    <div className="relative">
      <div
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={handleCardDoubleClick}
        className="bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-3 hover:shadow-md transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600 relative cursor-pointer"
        title="Double-click to open ticket"
      >
        {/* Drag Handle - Top area for dragging */}
        <div
          {...(dragListeners || {})}
          className={`absolute top-0 left-0 right-0 h-10 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} z-10`}
          title="Drag to move ticket"
          onDoubleClick={(e) => e.stopPropagation()}
        />
        
        {/* Card Content */}
        <div className="relative" style={{ zIndex: 3 }}>
        {/* Customer Name, Company & Serial */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
              {ticket.customerName || 'Unknown Customer'}
            </h4>
            {ticket.customerCompany && (
              <p className="text-xs text-gray-600 dark:text-gray-300 truncate font-medium">
                {ticket.customerCompany}
              </p>
            )}
            {ticket.deviceSerialNumber && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {ticket.deviceSerialNumber}
              </p>
            )}
          </div>
            <div className="ml-2 flex items-center space-x-1">
              {/* Tier Badge */}
              {tierBadge}
              {/* Urgency Badge */}
              {urgencyBadge && (
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${urgencyBadge.color} ${urgencyBadge.urgent ? 'animate-pulse' : ''}`}>
                  {urgencyBadge.label}
                </span>
              )}
              {/* Closure Status Badge */}
              {closureStatusBadge}
            </div>
        </div>

        {/* Title & Ticket ID */}
        <div className="mb-3">
          <p className="text-sm text-gray-800 dark:text-gray-200 leading-tight" style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {ticket.title}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {ticket.ticketNumber}
          </p>
        </div>

        {/* Bottom Row: Agent Avatar, Category Badge, Age */}
        <div className="flex items-center space-x-2">
          <div className="relative flex-shrink-0" style={{ zIndex: 20 }}>
            {renderAgentAvatar()}
            
            {/* Assignment Dropdown */}
            {showAssignDropdown && user?.permissions?.includes('tickets.edit') && (
              <div
                ref={dropdownRef}
                className="absolute bottom-full left-0 mb-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-600"
                style={{ zIndex: 100 }}
              >
                <div className="py-1 max-h-48 overflow-y-auto">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssign(null);
                    }}
                    disabled={loading}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <span className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-xs">
                      ×
                    </span>
                    <span>Unassign</span>
                  </button>
                  
                  {agents.map((agent) => {
                    let avatarUrl = agent.avatar || agent.avatarUrl;
                    // Convert relative avatar URL to full URL if needed
                    if (avatarUrl && avatarUrl.startsWith('/uploads/avatars/')) {
                      avatarUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${avatarUrl}`;
                    }
                    const initials = agent.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'AG';
                    
                    return (
                      <button
                        key={agent.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssign(agent.id);
                        }}
                        disabled={loading}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs overflow-hidden">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={agent.name || 'Agent'}
                              className="w-full h-full object-cover rounded-full"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full bg-primary-500 rounded-full flex items-center justify-center text-white ${avatarUrl ? 'hidden' : ''}`}>
                            {initials}
                          </div>
                        </div>
                        <span>{agent.name}</span>
                        {agent.isOnline && (
                          <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Category Badge - stretches from avatar to age badge */}
          <div className="flex items-center justify-between flex-1 min-w-0">
            {ticket.category && (
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 truncate flex-1 mr-2">
                {ticket.category}
              </span>
            )}

            {/* Age Badge - fixed width */}
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap flex-shrink-0">
              {formatTimeAgo(ticket.createdAt)}
            </span>
          </div>
        </div>
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && (() => {
        // Show tooltip on left for the last two columns to prevent cutoff
        const showOnLeft = columnIndex >= totalColumns - 2;
        
        return (
          <div
            ref={tooltipRef}
            className="absolute z-50 w-80 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl pointer-events-none"
            style={showOnLeft ? {
              top: '-8px',
              right: '100%',
              marginRight: '8px',
              transform: 'translateY(-50%)'
            } : {
              top: '-8px',
              left: '100%',
              marginLeft: '8px',
              transform: 'translateY(-50%)'
            }}
          >
            {/* Arrow */}
            <div className={`absolute top-1/2 transform -translate-y-1/2 ${showOnLeft ? 'right-0 translate-x-full' : 'left-0 -translate-x-full'}`}>
              <div className={`w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent ${showOnLeft ? 'border-l-8 border-l-gray-200 dark:border-l-gray-600' : 'border-r-8 border-r-gray-200 dark:border-r-gray-600'}`}></div>
              <div className={`absolute top-1/2 transform -translate-y-1/2 ${showOnLeft ? 'right-0 -translate-x-px' : 'left-0 translate-x-px'}`}>
                <div className={`w-0 h-0 border-t-7 border-t-transparent border-b-7 border-b-transparent ${showOnLeft ? 'border-l-7 border-l-white dark:border-l-gray-800' : 'border-r-7 border-r-white dark:border-r-gray-800'}`}></div>
              </div>
            </div>

            {/* Tooltip Content */}
            <div className="space-y-3">
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                {ticket.ticketNumber}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {getStatusDisplayName(ticket.status)}
              </p>
            </div>

            {/* Customer Info */}
            <div>
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Customer</h4>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {ticket.customerName || 'Unknown Customer'}
              </p>
              {ticket.customerCompany && (
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {ticket.customerCompany}
                </p>
              )}
              {ticket.customerEmail && (
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {ticket.customerEmail}
                </p>
              )}
            </div>

            {/* Device Info */}
            {(ticket.deviceModel || ticket.deviceSerialNumber) && (
              <div>
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Device</h4>
                {ticket.deviceModel && (
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {ticket.deviceModel}
                  </p>
                )}
                {ticket.deviceSerialNumber && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    S/N: {ticket.deviceSerialNumber}
                  </p>
                )}
              </div>
            )}

            {/* Title & Description */}
            <div>
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Issue</h4>
              <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                {ticket.title}
              </p>
              {ticket.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {ticket.description}
                </p>
              )}
            </div>

            {/* Agent Info */}
            {ticket.agent && (
              <div>
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Assigned Agent</h4>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {ticket.agent.name}
                </p>
              </div>
            )}

            {/* Priority & Category */}
            <div className="flex justify-between">
              <div>
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority?.charAt(0).toUpperCase() + ticket.priority?.slice(1)}
                </span>
              </div>
              {ticket.category && (
                <div>
                  <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Category</h4>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {ticket.category}
                  </span>
                </div>
              )}
            </div>

            {/* Timestamps */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Created:</span>
                  <p className="text-gray-600 dark:text-gray-400">{formatCreatedDate(ticket.createdAt)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Updated:</span>
                  <p className="text-gray-600 dark:text-gray-400">{formatCreatedDate(ticket.updatedAt)}</p>
                </div>
              </div>
            </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TicketCard;
